import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronRight, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { isInertMenuItem, menuPickPath } from './navLookup.js';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

// Space kept between a flyout and the bottom of the window.
const FLYOUT_EDGE = 8;

// Dispatch a menu pick: leaf items with `openInNewTab` open in a new browser
// tab (matching the bottom HelpPopupButton's behavior for Docs); everything
// else routes in-app via `onPick` (see menuPickPath for where a row goes).
const dispatchPick = (item, onPick) => {
    const target = menuPickPath(item);
    if (!target) return;
    if (!item.subItems?.length && item.openInNewTab) {
        window.open(target, '_blank', 'noopener,noreferrer');
        return;
    }
    onPick(target);
};

const itemClass = (item, isCurrent = false) => [
    'page-menu-item',
    isCurrent && 'page-menu-item--current',
    item.isLocked && 'page-menu-item--locked',
    item.isPlaceholder && 'page-menu-item--placeholder',
].filter(Boolean).join(' ');

const HoverFlyout = ({ targetRect, level = 0, children }) => {
    const ref = useRef(null);
    const [top, setTop] = useState(null);
    // Measured before paint, so a long list (every game under Games) slides up onto the screen instead of running off it.
    useLayoutEffect(() => {
        if (!targetRect || !ref.current) return;
        const height = ref.current.offsetHeight;
        setTop(Math.max(FLYOUT_EDGE, Math.min(targetRect.top, window.innerHeight - height - FLYOUT_EDGE)));
    }, [targetRect]);
    if (!targetRect) return null;
    const style = {
        position: 'fixed',
        left: `${targetRect.right + 6}px`,
        top: `${top ?? targetRect.top}px`,
        zIndex: 2000 + level,
    };
    return createPortal(<div ref={ref} style={style}>{children}</div>, document.body);
};

const FlyoutMenu = ({ items, onPick, level, closeAll, closeTimerRef }) => {
    const [hovered, setHovered] = useState(null);
    const [hoveredRect, setHoveredRect] = useState(null);

    return (
        <div className="page-menu-flyout">
            {items.map(sub => {
                const hasChildren = sub.subItems?.length > 0;
                return (
                    <div
                        key={sub.path || sub.label}
                        className={itemClass(sub)}
                        aria-disabled={isInertMenuItem(sub) || undefined}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (isInertMenuItem(sub)) return;
                            dispatchPick(sub, onPick);
                            closeAll();
                        }}
                        onMouseEnter={(e) => {
                            if (hasChildren) {
                                setHovered(sub);
                                setHoveredRect(e.currentTarget.getBoundingClientRect());
                            } else {
                                setHovered(null);
                                setHoveredRect(null);
                            }
                        }}
                    >
                        <span className="page-menu-item-icon">{sub.icon}</span>
                        <span className="page-menu-item-label">{sub.label}</span>
                        {hasChildren && (
                            <FontAwesomeIcon icon={faChevronRight} className="page-menu-item-arrow" />
                        )}
                    </div>
                );
            })}
            {hovered && hoveredRect && (
                <HoverFlyout targetRect={hoveredRect} level={level + 1}>
                    <div
                        className="page-menu-flyout-wrap"
                        onMouseEnter={() => clearTimeout(closeTimerRef.current)}
                        onMouseLeave={() => { closeTimerRef.current = setTimeout(closeAll, 120); }}
                    >
                        <FlyoutMenu
                            items={hovered.subItems}
                            onPick={onPick}
                            level={level + 1}
                            closeAll={closeAll}
                            closeTimerRef={closeTimerRef}
                        />
                    </div>
                </HoverFlyout>
            )}
        </div>
    );
};

const MenuItem = ({ item, isCurrent, onPick, onClose }) => {
    const hasChildren = item.subItems?.length > 0;
    const itemRef = useRef(null);
    const flyoutTimerRef = useRef(null);
    const [hoverRect, setHoverRect] = useState(null);

    useEffect(() => () => clearTimeout(flyoutTimerRef.current), []);

    const handleClick = (e) => {
        e.stopPropagation();
        if (isInertMenuItem(item)) return;
        dispatchPick(item, onPick);
        onClose();
    };

    const handleMouseEnter = () => {
        if (hasChildren && itemRef.current) {
            clearTimeout(flyoutTimerRef.current);
            setHoverRect(itemRef.current.getBoundingClientRect());
        }
    };

    const handleMouseLeave = () => {
        if (hasChildren) {
            flyoutTimerRef.current = setTimeout(() => setHoverRect(null), 120);
        }
    };

    return (
        <>
            <div
                ref={itemRef}
                className={itemClass(item, isCurrent)}
                aria-disabled={isInertMenuItem(item) || undefined}
                onClick={handleClick}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <span className="page-menu-item-icon">{item.icon}</span>
                <span className="page-menu-item-label">{item.label}</span>
                {hasChildren ? (
                    <FontAwesomeIcon icon={faChevronRight} className="page-menu-item-arrow" />
                ) : isCurrent ? (
                    <FontAwesomeIcon icon={faCheck} className="page-menu-item-check" />
                ) : null}
            </div>
            {hasChildren && hoverRect && (
                <HoverFlyout targetRect={hoverRect}>
                    <div
                        className="page-menu-flyout-wrap"
                        onMouseEnter={() => clearTimeout(flyoutTimerRef.current)}
                        onMouseLeave={() => { flyoutTimerRef.current = setTimeout(() => setHoverRect(null), 120); }}
                    >
                        <FlyoutMenu
                            items={item.subItems}
                            onPick={onPick}
                            level={1}
                            closeAll={() => { setHoverRect(null); onClose(); }}
                            closeTimerRef={flyoutTimerRef}
                        />
                    </div>
                </HoverFlyout>
            )}
        </>
    );
};

export const PageMenu = ({
    context,
    currentPath,
    onPick,
    onSearch,
    onClose,
}) => {
    const sisters = context?.sisters || [];
    const parentSisters = context?.parentSisters || [];

    return (
        <div className="page-menu" role="menu">
            {sisters.length > 0 && (
                <div className="page-menu-section">
                    <div className="page-menu-section-label">
                        In {context.parentLabel}
                    </div>
                    {sisters.map(item => (
                        <MenuItem
                            key={item.path || item.label}
                            item={item}
                            isCurrent={item.path === currentPath}
                            onPick={onPick}
                            onClose={onClose}
                        />
                    ))}
                </div>
            )}
            {parentSisters.length > 0 && (
                <div className="page-menu-section">
                    <div className="page-menu-section-label">
                        {context.grandparentLabel ? `Other in ${context.grandparentLabel}` : 'Other'}
                    </div>
                    {parentSisters.map(item => (
                        <MenuItem
                            key={item.path || item.label}
                            item={item}
                            isCurrent={false}
                            onPick={onPick}
                            onClose={onClose}
                        />
                    ))}
                </div>
            )}
            {(sisters.length > 0 || parentSisters.length > 0) && (
                <div className="page-menu-divider" />
            )}
            <div
                className="page-menu-item page-menu-search"
                onClick={(e) => { e.stopPropagation(); onSearch(); onClose(); }}
            >
                <span className="page-menu-item-icon">
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                </span>
                <span className="page-menu-item-label">Search anywhere...</span>
                <kbd className="page-menu-shortcut">{isMac ? '⌘K' : 'Ctrl K'}</kbd>
            </div>
        </div>
    );
};

export default PageMenu;
